/**
 * 剧本 API 服务
 * 对接后端 /api/v1/screenplays
 */
import apiClient from './client';
import { uploadDirectToOss } from './direct-upload';
import type {
  Screenplay,
  ScreenplayListParams,
  ScreenplayListResponse,
  CreateScreenplayDto,
  ImportStoryboardDto,
  ImportStoryboardResponse,
  UpdateScreenplayDto,
  UploadScreenplayDto,
  UploadScreenplayResponse,
  ParseStatusResponse,
  ScreenplayScene,
  ScreenplayCharacter,
  ScreenplayLocation,
  ScreenplayProp,
  PaginatedResponse,
} from '@/types/screenplay';

export const SCREENPLAY_LIST_MAX_PAGE_SIZE = 100;

function normalizeScreenplayListPageSize(pageSize?: number): number {
  if (!pageSize || pageSize <= 0) {
    return 20;
  }
  return Math.min(pageSize, SCREENPLAY_LIST_MAX_PAGE_SIZE);
}

/**
 * 剧本 API
 */
export const screenplayApi = {
  // ==================== 剧本 CRUD ====================

  /**
   * 获取剧本列表
   * GET /api/v1/screenplays
   */
  async getAll(params: ScreenplayListParams): Promise<ScreenplayListResponse> {
    return apiClient.get('/screenplays', {
      params: {
        projectId: params.projectId,
        status: params.status,
        page: params.page || 1,
        pageSize: normalizeScreenplayListPageSize(params.pageSize),
      },
    }) as Promise<ScreenplayListResponse>;
  },

  /**
   * 获取单个剧本
   * GET /api/v1/screenplays/:id
   */
  async getById(id: string): Promise<Screenplay> {
    return apiClient.get(`/screenplays/${id}`) as Promise<Screenplay>;
  },

  async getScenes(screenplayId: string): Promise<ScreenplayScene[]> {
    const response = (await apiClient.get(`/screenplays/${screenplayId}/scenes`)) as {
      items: ScreenplayScene[];
      total: number;
    };
    return response.items ?? [];
  },

  async recommendAssetFieldSuggestions(
    screenplayId: string,
    body: {
      group: 'characters' | 'locations' | 'props';
      name: string;
      occupiedLabels: string[];
      shotAppearances?: Array<{
        sceneNumber: string;
        characterNames: readonly string[];
        locationNames: readonly string[];
        propNames: readonly string[];
      }>;
    }
  ): Promise<{
    suggestions: Array<{
      sceneLabels: string[];
      assignment: string;
      fields: Array<{ label: string; value: string }>;
    }>;
    assignment: string;
    sceneLabels: string[];
  }> {
    return apiClient.post(`/screenplays/${screenplayId}/asset-field-suggestions`, body) as Promise<{
      suggestions: Array<{
        sceneLabels: string[];
        assignment: string;
        fields: Array<{ label: string; value: string }>;
      }>;
      assignment: string;
      sceneLabels: string[];
    }>;
  },

  /**
   * 创建文本剧本
   * POST /api/v1/screenplays
   */
  async create(data: CreateScreenplayDto, activeOrgId?: string | null): Promise<Screenplay> {
    return apiClient.post(
      '/screenplays',
      {
        projectId: data.projectId,
        name: data.name,
        content: data.content,
        memberSource: data.memberSource,
        subprojectBootstrap: data.subprojectBootstrap ?? undefined,
      },
      activeOrgId ? { headers: { 'X-Active-Org-Id': activeOrgId } } : undefined
    ) as Promise<Screenplay>;
  },

  /**
   * 更新剧本
   * PATCH /api/v1/screenplays/:id
   */
  async update(id: string, data: UpdateScreenplayDto): Promise<Screenplay> {
    return apiClient.patch(`/screenplays/${id}`, {
      name: data.name,
      content: data.content,
      changeSummary: data.changeSummary,
    }) as Promise<Screenplay>;
  },

  /**
   * 删除剧本（软删除）
   * DELETE /api/v1/screenplays/:id
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/screenplays/${id}`);
  },

  // ==================== 文件上传与解析 ====================

  /**
   * 上传并解析剧本文件
   * POST /api/v1/screenplays/upload-and-parse
   *
   * 智能路由策略：
   * - TXT/Markdown: 同步解析，立即返回内容
   * - DOCX/PDF/RTF/DOC: 异步解析，返回任务 ID
   */
  async uploadAndParse(
    data: UploadScreenplayDto,
    onProgress?: (progress: number) => void,
    activeOrgId?: string | null
  ): Promise<UploadScreenplayResponse> {
    return uploadDirectToOss<UploadScreenplayResponse>({
      file: data.file,
      businessType: 'screenplay_upload',
      businessPayload: {
        projectId: data.projectId,
        name: data.name,
        fileName: data.file.name,
        activeOrgId,
        memberSource: data.memberSource,
        subprojectBootstrap: data.subprojectBootstrap,
      },
      onProgress,
    });
  },

  /**
   * 上传分镜表格并由后端导入
   * POST /api/v1/screenplays/import-storyboard
   */
  async importStoryboard(
    data: ImportStoryboardDto,
    onProgress?: (progress: number) => void,
    activeOrgId?: string | null
  ): Promise<ImportStoryboardResponse> {
    return uploadDirectToOss<ImportStoryboardResponse>({
      file: data.file,
      businessType: 'storyboard_import',
      businessPayload: {
        projectId: data.projectId,
        parentProjectId: data.parentProjectId,
        name: data.name,
        fileName: data.file.name,
        activeOrgId,
        memberSource: data.memberSource,
        subprojectBootstrap: data.subprojectBootstrap,
      },
      onProgress,
    });
  },

  /**
   * 下载分镜导入模板
   * GET /api/v1/screenplays/storyboard-template
   */
  async downloadTemplate(): Promise<Blob> {
    return apiClient.get('/screenplays/storyboard-template', {
      responseType: 'blob',
    }) as Promise<Blob>;
  },

  /**
   * 查询解析状态
   * GET /api/v1/screenplays/:id/parse-status
   */
  async getParseStatus(id: string): Promise<ParseStatusResponse> {
    return apiClient.get(`/screenplays/${id}/parse-status`) as Promise<ParseStatusResponse>;
  },

  /**
   * 手动触发文件解析
   * POST /api/v1/screenplays/:id/parse-file
   */
  async parseFile(
    id: string,
    force = false,
    activeOrgId?: string | null
  ): Promise<{ screenplayId: string; parsingStatus: string; taskId: string }> {
    return apiClient.post(
      `/screenplays/${id}/parse-file`,
      { force },
      activeOrgId ? { headers: { 'X-Active-Org-Id': activeOrgId } } : undefined
    ) as Promise<{
      screenplayId: string;
      parsingStatus: string;
      taskId: string;
    }>;
  },

  // ==================== 剧本元素管理 ====================

  /**
   * 获取角色列表
   * GET /api/v1/screenplays/:id/characters
   */
  async getCharacters(
    screenplayId: string,
    params?: { page?: number; pageSize?: number; search?: string }
  ): Promise<PaginatedResponse<ScreenplayCharacter>> {
    return apiClient.get(`/screenplays/${screenplayId}/characters`, {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        search: params?.search,
      },
    }) as Promise<PaginatedResponse<ScreenplayCharacter>>;
  },

  /**
   * 获取场景列表
   * GET /api/v1/screenplays/:id/locations
   */
  async getLocations(
    screenplayId: string,
    params?: { page?: number; pageSize?: number; search?: string }
  ): Promise<PaginatedResponse<ScreenplayLocation>> {
    return apiClient.get(`/screenplays/${screenplayId}/locations`, {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        search: params?.search,
      },
    }) as Promise<PaginatedResponse<ScreenplayLocation>>;
  },

  /**
   * 获取道具列表
   * GET /api/v1/screenplays/:id/props
   */
  async getProps(
    screenplayId: string,
    params?: { page?: number; pageSize?: number; search?: string }
  ): Promise<PaginatedResponse<ScreenplayProp>> {
    return apiClient.get(`/screenplays/${screenplayId}/props`, {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        search: params?.search,
      },
    }) as Promise<PaginatedResponse<ScreenplayProp>>;
  },

  /**
   * 更新剧本排序
   * PUT /api/v1/screenplays/reorder
   */
  async reorder(projectId: string, orderedIds: string[]): Promise<void> {
    await apiClient.put('/screenplays/reorder', {
      projectId,
      orderedIds,
    });
  },
};
